import React from "react";
import { Helmet } from "react-helmet-async";
import * as FaIcons from "react-icons/fa";
import { getDestinationHeroImage } from "../data/destinationHeroImages";
import { trackBookingClick, trackWhatsAppClick } from "../utils/analytics";

const larnacaAirportTaxiContent = {
  intro: {
    eyebrow: "Larnaca Airport taxi guide",
    title: "What to expect when booking a Larnaca Airport taxi",
    text:
      "Larnaca Airport is the busiest international airport in Cyprus, and many Limassol journeys depend on good arrival timing. A private taxi keeps the transfer simple because the pickup is planned around your flight, luggage, passengers, and exact Limassol address.",
  },
  serviceCards: [
    {
      title: "Meet and greet on arrival",
      text:
        "For airport pickups, your driver can meet you inside the arrivals area after baggage claim. This is useful for first-time visitors, families, business guests, and anyone who prefers not to search for transport after landing.",
    },
    {
      title: "Flight monitoring",
      text:
        "Share your flight number when booking so the pickup can be planned around the real arrival time. If the aircraft lands early or late, the driver has clearer context before heading to the meeting point.",
    },
    {
      title: "Clear arrival process",
      text:
        "After you land, collect your luggage, pass arrivals, and check your phone for any driver message. If the airport is busy, WhatsApp is the fastest way to confirm you are ready to leave.",
    },
    {
      title: "Driver waiting point",
      text:
        "The usual meeting point is the public arrivals hall at Larnaca Airport. Your confirmation message can include the driver name, vehicle details, and exact instructions for where to meet.",
    },
  ],
  detailSection: {
    eyebrow: "Arrival transfer details",
    title: "From the arrivals hall to your Limassol address",
    text:
      "A good Larnaca Airport taxi booking removes the uncertain parts of arrival day: where to meet, how long to wait, what happens with luggage, and how the driver knows your flight status.",
  },
  journeyCards: [
    {
      title: "Direct journey to Limassol",
      text:
        "The route from Larnaca Airport to Limassol normally follows the A3 and A1 motorway. Most transfers go directly to hotels, apartments, offices, Limassol Marina, Germasogeia, Agios Tychonas, or nearby coastal areas.",
    },
    {
      title: "Fixed price guidance",
      text:
        "The published price gives travelers a clear starting point before booking. The final quote should confirm pickup time, destination area, passenger count, luggage, child seats, and any special arrival requirements.",
    },
    {
      title: "Child seats by request",
      text:
        "Families should request child seats before travel and include the child's age. Advance notice helps match the right seat type and avoids last-minute uncertainty at the airport.",
    },
    {
      title: "Business traveller support",
      text:
        "For business trips, a pre-booked taxi helps protect meeting schedules after landing. Direct airport pickup, clear communication, and a quiet transfer are especially useful for guests heading to Limassol offices or hotels.",
    },
  ],
  popularDestinations: [
    {
      name: "Limassol Marina",
      text:
        "A common drop-off for hotels, restaurants, residences, and yacht arrivals in the city center.",
      href: "/things-to-do-in-limassol",
    },
    {
      name: "Germasogeia hotels",
      text:
        "A practical transfer area for beachfront hotels, serviced apartments, and business stays.",
      href: "/limassol-hotels",
    },
    {
      name: "Agios Tychonas",
      text:
        "Popular with resort guests who want a direct taxi from the airport to the eastern Limassol coast.",
      href: "/limassol-hotels",
    },
    {
      name: "City center and Old Town",
      text:
        "Useful for travelers staying near offices, restaurants, nightlife, or the historic center.",
      href: "/taxi-limassol",
    },
  ],
  destinationsSection: {
    eyebrow: "Popular Limassol drop-offs",
    title: "Common destinations from Larnaca Airport",
    text:
      "Most Larnaca Airport taxi bookings to Limassol are direct transfers to hotel areas, seafront neighborhoods, offices, and private accommodation. Confirm the full address before travel so the driver can plan the most practical drop-off.",
  },
  internalLinks: [
    {
      label: "Larnaca Airport to Limassol",
      href: "/larnaca-airport-to-limassol",
    },
    {
      label: "Limassol to Larnaca Airport Taxi",
      href: "/limassol-to-larnaca-airport-taxi",
    },
    {
      label: "Cyprus Airport Transfers",
      href: "/cyprus-airport-transfers",
    },
  ],
  faqs: [
    {
      question: "Do you offer meet and greet at Larnaca Airport?",
      answer:
        "Yes. Meet and greet can be arranged for arrivals when you provide your flight details and passenger information. The driver can meet you in the arrivals hall after baggage claim.",
    },
    {
      question: "Where does the driver wait at Larnaca Airport?",
      answer:
        "The standard meeting area is the public arrivals hall. Your booking confirmation can include driver and vehicle details so you know who to look for when you exit customs.",
    },
    {
      question: "What happens if my flight to Larnaca is delayed?",
      answer:
        "Send your flight number when booking. Flight monitoring helps the driver follow the arrival time and adjust pickup planning when the aircraft is delayed or lands earlier than expected.",
    },
    {
      question: "Can I book a Larnaca Airport taxi with child seats?",
      answer:
        "Yes, child seats can be requested in advance. Include the child's age and seat requirement when booking so availability can be checked before your transfer.",
    },
    {
      question: "Is the Larnaca Airport taxi price fixed?",
      answer:
        "The listed fare is a guide for a standard private transfer. The confirmed price depends on the exact Limassol destination, passenger count, luggage, pickup time, and any special requests.",
    },
    {
      question: "Is this service suitable for business travellers?",
      answer:
        "Yes. Business travellers often book private Larnaca Airport taxis for punctual pickup, direct transfers to Limassol, and simple communication after landing.",
    },
  ],
};

const paphosAirportTaxiContent = {
  intro: {
    eyebrow: "Paphos Airport taxi guide",
    title: "Private Paphos Airport taxis for western Cyprus arrivals",
    text:
      "Paphos Airport serves western Cyprus, resort areas, golf communities, and many visitors continuing east to Limassol. A pre-booked taxi is especially useful when your accommodation is outside the airport bus routes or when you arrive with luggage, children, or late-night plans.",
  },
  serviceCards: [
    {
      title: "Meet and greet at Paphos Airport",
      text:
        "Your driver can meet you after arrivals so the first part of the journey feels organized. This helps guests heading to Aphrodite Hills, Coral Bay, Paphos hotels, or Limassol avoid searching for transport outside the terminal.",
    },
    {
      title: "Flight monitoring for arrivals",
      text:
        "Paphos flight schedules can vary by season, charter traffic, and late evening arrivals. Sending your flight number gives the driver a practical way to follow the landing time before positioning for pickup.",
    },
    {
      title: "Simple arrivals pickup process",
      text:
        "After passport control and baggage collection, check your phone and move toward the arrivals exit. Your confirmation can include the driver name and vehicle details so you know exactly how to connect.",
    },
    {
      title: "Clear terminal meeting point",
      text:
        "The usual meeting area is the public arrivals hall at Paphos Airport. If the terminal is crowded, WhatsApp communication helps coordinate the final few meters without asking you to leave the airport area alone.",
    },
  ],
  detailSection: {
    eyebrow: "Paphos arrival transfer details",
    title: "From Paphos Airport to Limassol, resorts, and hotels",
    text:
      "Paphos Airport transfers often serve two different needs: short resort journeys around Paphos and longer private transfers to Limassol. Good pickup planning makes both easier, especially for evening flights and family holidays.",
  },
  journeyCards: [
    {
      title: "Journey to Limassol",
      text:
        "The road from Paphos Airport to Limassol usually runs east via the A6 motorway. It is a direct intercity route for travelers going to Limassol Marina, Germasogeia, Agios Tychonas, business hotels, and seafront apartments.",
    },
    {
      title: "Fixed price confirmation",
      text:
        "The advertised fare is a guide for standard private transfers. A confirmed quote should include your Paphos pickup point, final destination, arrival time, luggage, passengers, and any stops or child seat requests.",
    },
    {
      title: "Families and child seats",
      text:
        "Many Paphos arrivals are family holidays, so child seats should be requested before the travel date. Include ages and passenger numbers so the vehicle can be planned around luggage and seat space.",
    },
    {
      title: "Business travel from the west",
      text:
        "For business travelers landing at Paphos, a private taxi gives a quieter transfer to Limassol meetings, conference hotels, or offices without waiting for shared transport or rental car paperwork.",
    },
    {
      title: "Night arrivals",
      text:
        "Late flights into Paphos can make onward transport harder to arrange on arrival. Booking ahead keeps the transfer planned before the terminal quiets down and reduces uncertainty after a long flight.",
    },
    {
      title: "Resort travel context",
      text:
        "Paphos Airport is convenient for Coral Bay, Aphrodite Hills, Pissouri, Kato Paphos, and western resort stays. For Limassol guests, it is also a practical airport when flight schedules or routes are better than Larnaca.",
    },
  ],
  popularDestinations: [
    {
      name: "Limassol Marina",
      text:
        "A frequent eastbound drop-off for visitors continuing from Paphos Airport to central Limassol hotels, residences, and restaurants.",
      href: "/things-to-do-in-limassol",
    },
    {
      name: "Aphrodite Hills",
      text:
        "A well-known resort and golf destination between Paphos and Limassol, often booked for villas, weddings, and leisure trips.",
      href: "/cyprus",
    },
    {
      name: "Coral Bay",
      text:
        "A popular western Cyprus resort area for beach holidays, family accommodation, and villa stays north of Paphos.",
      href: "/cyprus",
    },
    {
      name: "Paphos hotels",
      text:
        "Useful for arrivals staying around Kato Paphos, the harbour, Tombs of the Kings, or nearby coastal hotel areas.",
      href: "/cyprus",
    },
    {
      name: "Pissouri Bay",
      text:
        "A practical stop between Paphos and Limassol for villas, beach stays, and quieter resort accommodation.",
      href: "/cyprus",
    },
    {
      name: "Limassol beachfront hotels",
      text:
        "A common destination for guests transferring from Paphos Airport to Germasogeia, Agios Tychonas, and seafront resorts.",
      href: "/limassol-hotels",
    },
  ],
  destinationsSection: {
    eyebrow: "Popular Paphos Airport destinations",
    title: "Transfers to Limassol, western resorts, and coastal hotels",
    text:
      "Paphos Airport taxi bookings often connect the airport with resort accommodation spread across western Cyprus. Confirming the exact hotel, villa, or apartment address helps the driver plan the most direct arrival route.",
  },
  internalLinks: [
    {
      label: "Paphos Airport to Limassol",
      href: "/paphos-airport-to-limassol",
    },
    {
      label: "Limassol to Paphos Airport Taxi",
      href: "/limassol-to-paphos-airport-taxi",
    },
    {
      label: "Cyprus Airport Transfers",
      href: "/cyprus-airport-transfers",
    },
    {
      label: "Limassol Hotels",
      href: "/limassol-hotels",
    },
  ],
  faqs: [
    {
      question: "Do you offer meet and greet at Paphos Airport?",
      answer:
        "Yes. Meet and greet can be arranged for Paphos Airport arrivals. Send your flight number, passenger count, and destination so the driver can meet you after baggage collection.",
    },
    {
      question: "Where does the driver wait at Paphos Airport?",
      answer:
        "The usual waiting point is the public arrivals hall. Your confirmation can include driver and vehicle details, and WhatsApp can be used if the terminal is busy.",
    },
    {
      question: "Can you monitor delayed Paphos flights?",
      answer:
        "Yes. Flight monitoring is available when you provide the flight number. This is useful for seasonal flights, evening arrivals, and any schedule changes before landing.",
    },
    {
      question: "How long is the taxi journey from Paphos Airport to Limassol?",
      answer:
        "Journey time depends on the exact Limassol address, traffic, and arrival time. The route is usually a direct eastbound motorway transfer from Paphos Airport toward Limassol.",
    },
    {
      question: "Can I book child seats for a Paphos Airport transfer?",
      answer:
        "Yes. Request child seats in advance and include the children's ages. This helps confirm the right seat type and enough space for luggage and passengers.",
    },
    {
      question: "Do you handle night arrivals at Paphos Airport?",
      answer:
        "Yes. Night arrivals can be booked in advance, which is recommended because onward transport options are more limited late in the evening.",
    },
    {
      question: "Can you transfer from Paphos Airport to Aphrodite Hills or Coral Bay?",
      answer:
        "Yes. Paphos Airport taxis can be arranged for western Cyprus resort destinations including Aphrodite Hills, Coral Bay, Pissouri, Paphos hotels, and Limassol.",
    },
  ],
};

const limassolToNicosiaTaxiContent = {
  intro: {
    eyebrow: "Limassol to Nicosia taxi guide",
    title: "Private intercity transfers from Limassol to Nicosia",
    text:
      "The Limassol to Nicosia route is one of the most practical private taxi journeys in Cyprus for meetings, appointments, family visits, and day trips. A reserved transfer gives you a direct door-to-door journey without parking concerns, bus changes, or waiting for a return ride.",
  },
  serviceCards: [
    {
      title: "Journey overview",
      text:
        "Most trips travel north from Limassol toward Nicosia using the main motorway network. The route is straightforward, but pickup time matters because commuter traffic can build near Limassol exits and Nicosia entry points.",
    },
    {
      title: "Business travel between cities",
      text:
        "Many passengers use this transfer for meetings, conferences, legal appointments, and same-day office visits. A private taxi lets you leave at a planned time and arrive directly at the building or reception area.",
    },
    {
      title: "Government and embassy visits",
      text:
        "Nicosia is the administrative center of Cyprus, with ministries, government offices, embassies, consulates, and professional service firms. Confirm the exact entrance or nearby landmark when booking to make drop-off easier.",
    },
    {
      title: "Corporate areas and appointments",
      text:
        "Transfers can be planned for offices around the city center, Strovolos, Engomi, Aglantzia, and business districts near main roads. For time-sensitive appointments, allow margin for urban traffic and parking pressure near busy buildings.",
    },
  ],
  detailSection: {
    eyebrow: "Intercity transfer planning",
    title: "Door-to-door travel without changing vehicles",
    text:
      "A Limassol to Nicosia taxi is most useful when the journey has a fixed appointment time, multiple passengers, luggage, documents, or a precise address. The driver collects you from your chosen Limassol pickup point and takes you directly to your Nicosia destination.",
  },
  journeyCards: [
    {
      title: "Fixed price private transfer",
      text:
        "The published price provides a clear guide before booking. Your confirmed fare should account for the exact pickup, Nicosia drop-off, waiting time needs, passenger count, luggage, and any return transfer plan.",
    },
    {
      title: "Door-to-door pickup",
      text:
        "Pickup can be arranged from Limassol hotels, homes, offices, Limassol Marina, serviced apartments, or business locations. Sharing a precise map point helps avoid delays on narrow streets or large hotel entrances.",
    },
    {
      title: "Best departure times",
      text:
        "For morning appointments in Nicosia, leaving earlier is usually safer because commuter traffic can affect the approach to the capital. Midday transfers are often more relaxed, while late afternoon returns can be slower.",
    },
    {
      title: "Passenger comfort and luggage",
      text:
        "Private intercity taxis are helpful when passengers have suitcases, business equipment, shopping bags, or documents. Mention luggage volume in advance so the vehicle can be matched to the journey.",
    },
    {
      title: "Return transfers",
      text:
        "If you need to return to Limassol the same day, request a return transfer when booking. This is practical for appointments, embassy visits, medical consultations, university trips, and business meetings with uncertain end times.",
    },
    {
      title: "Family and tourist trips",
      text:
        "Nicosia is also useful for sightseeing, shopping, museums, Ledra Street, old city walks, and family visits. A private transfer keeps the schedule flexible without needing to manage intercity buses or parking.",
    },
  ],
  popularDestinations: [
    {
      name: "Limassol Marina pickup",
      text:
        "A convenient starting point for visitors staying near restaurants, apartments, offices, and the old port area.",
      href: "/things-to-do-in-limassol",
    },
    {
      name: "Limassol hotels",
      text:
        "Common pickups include beachfront hotels, business hotels, serviced apartments, and resorts along the coastal road.",
      href: "/limassol-hotels",
    },
    {
      name: "Germasogeia and Agios Tychonas",
      text:
        "Useful pickup areas for seafront accommodation, residential stays, and travelers starting from eastern Limassol.",
      href: "/taxi-limassol",
    },
    {
      name: "Nicosia city center",
      text:
        "A frequent drop-off for offices, shops, restaurants, old city visits, and appointments near the capital's central districts.",
      href: "/cyprus",
    },
    {
      name: "Embassies and ministries",
      text:
        "Plan extra time for security checks, exact entrances, and busy streets around official buildings and diplomatic areas.",
      href: "/cyprus",
    },
    {
      name: "Engomi and Strovolos",
      text:
        "Popular Nicosia drop-off areas for universities, clinics, offices, embassies, and residential appointments.",
      href: "/cyprus",
    },
  ],
  destinationsSection: {
    eyebrow: "Pickup and drop-off areas",
    title: "Popular Limassol starting points and Nicosia destinations",
    text:
      "A precise address is the most important detail for an intercity taxi. Limassol pickup points can be hotels, offices, homes, or marina apartments, while Nicosia drop-offs often require accurate building names, district details, or nearby landmarks.",
  },
  internalLinks: [
    {
      label: "Taxi Limassol",
      href: "/taxi-limassol",
    },
    {
      label: "Things To Do In Limassol",
      href: "/things-to-do-in-limassol",
    },
    {
      label: "Cyprus Guide",
      href: "/cyprus",
    },
    {
      label: "Cyprus Airport Transfers",
      href: "/cyprus-airport-transfers",
    },
  ],
  faqs: [
    {
      question: "Can I book a private taxi from Limassol to Nicosia?",
      answer:
        "Yes. You can book a private door-to-door taxi from Limassol to Nicosia for business meetings, appointments, family visits, shopping, sightseeing, or same-day return travel.",
    },
    {
      question: "Is the Limassol to Nicosia taxi price fixed?",
      answer:
        "The listed price is a guide for a standard private transfer. The confirmed fare depends on the exact pickup point, Nicosia destination, passengers, luggage, waiting time, and whether you need a return transfer.",
    },
    {
      question: "What time should I leave Limassol for a Nicosia appointment?",
      answer:
        "For morning appointments, it is sensible to allow extra time for commuter traffic near Nicosia. Share your appointment time when booking so pickup can be planned with a realistic margin.",
    },
    {
      question: "Can the driver drop me at an embassy or government office?",
      answer:
        "Yes. Provide the full office name, address, and any entrance instructions. Some official buildings have limited stopping areas, so a nearby landmark can help make the drop-off smoother.",
    },
    {
      question: "Can I book a return taxi from Nicosia to Limassol?",
      answer:
        "Yes. Return transfers can be arranged in advance. This is useful when you want the same transport plan after a meeting, medical appointment, embassy visit, or day trip.",
    },
    {
      question: "Is this route suitable for families and tourists?",
      answer:
        "Yes. Families and tourists use this route for shopping, sightseeing, old city visits, museums, and family appointments. Mention children, luggage, or extra stops before confirmation.",
    },
  ],
};

const limassolToAyiaNapaTaxiContent = {
  intro: {
    eyebrow: "Limassol to Ayia Napa taxi guide",
    title: "Private resort transfers from Limassol to Ayia Napa",
    text:
      "A Limassol to Ayia Napa taxi is a practical choice for travelers heading east for beach hotels, villas, nightlife, marina visits, family holidays, and resort stays. A private transfer keeps the journey direct, with pickup from your Limassol address and drop-off at the exact Ayia Napa location you choose.",
  },
  serviceCards: [
    {
      title: "Journey overview",
      text:
        "The route from Limassol to Ayia Napa usually follows the eastbound motorway network before continuing toward the Famagusta resort area. It is a longer cross-island journey, so planning pickup time and comfort matters more than for a short city taxi.",
    },
    {
      title: "Resort and nightlife travel",
      text:
        "Ayia Napa is popular for beach clubs, restaurants, nightlife, summer events, and group holidays. A pre-booked taxi helps passengers travel directly without arranging parking, designated drivers, or multiple connections late in the day.",
    },
    {
      title: "Hotels, villas, and beach transfers",
      text:
        "Transfers can be arranged to hotel entrances, private villas, apartment complexes, beach areas, and resort neighborhoods. Sharing the property name and map location is especially useful for villas or small accommodation roads.",
    },
    {
      title: "Nissi Beach and marina areas",
      text:
        "Popular drop-offs include Nissi Beach, Ayia Napa Marina, the harbour area, central resort hotels, and nearby coastal streets. For beach pickups or busy summer evenings, agree on a clear meeting point before travel.",
    },
  ],
  detailSection: {
    eyebrow: "East coast transfer planning",
    title: "A direct private taxi for beach, resort, and villa travel",
    text:
      "Private transfers are useful when the journey includes luggage, children, evening plans, or a specific resort check-in time. The driver collects you from your Limassol pickup point and takes you directly across Cyprus to Ayia Napa without shared stops.",
  },
  journeyCards: [
    {
      title: "Fixed price private transfer",
      text:
        "The advertised price gives a clear starting point before booking. Your confirmed fare should include the exact Limassol pickup, Ayia Napa destination, passenger count, luggage, travel time, and any return transfer needs.",
    },
    {
      title: "Door-to-door pickup",
      text:
        "Pickup can be arranged from Limassol hotels, homes, offices, Limassol Marina, beachfront apartments, or resort accommodation. A precise address helps the driver choose the best pickup position and avoid unnecessary waiting.",
    },
    {
      title: "Best departure times",
      text:
        "For summer weekends, beach days, and evening plans, it is sensible to leave with extra time. Traffic can build around Limassol exits, Larnaca approaches, and resort roads near Ayia Napa during busy holiday periods.",
    },
    {
      title: "Family travel and luggage comfort",
      text:
        "Families often choose a private taxi for beach bags, suitcases, pushchairs, and child seats. Mention passenger ages and luggage volume in advance so the vehicle can be planned around comfort rather than just distance.",
    },
    {
      title: "Return transfers",
      text:
        "Return taxis from Ayia Napa to Limassol can be arranged for the same day, the next morning, or the end of a holiday. Pre-booking is useful after events, late dinners, beach clubs, or villa check-outs.",
    },
    {
      title: "Resort arrival details",
      text:
        "Ayia Napa accommodation can range from large hotels to private villas on quieter roads. Sending the hotel name, villa pin, or reception instructions helps make the final drop-off smoother after the long journey.",
    },
  ],
  popularDestinations: [
    {
      name: "Limassol Marina pickup",
      text:
        "A common starting point for visitors staying near the old port, restaurants, serviced apartments, and central nightlife.",
      href: "/things-to-do-in-limassol",
    },
    {
      name: "Limassol beachfront hotels",
      text:
        "Useful for guests leaving from Germasogeia, Agios Tychonas, seafront resorts, and eastern Limassol accommodation.",
      href: "/limassol-hotels",
    },
    {
      name: "Private homes and villas",
      text:
        "Door-to-door pickup works well for residential addresses, villa stays, and groups traveling with luggage or beach equipment.",
      href: "/taxi-limassol",
    },
    {
      name: "Nissi Beach",
      text:
        "One of Ayia Napa's best-known beach areas, often requested for hotel stays, beach days, and summer holiday transfers.",
      href: "/cyprus",
    },
    {
      name: "Ayia Napa Marina",
      text:
        "A popular drop-off for dining, marina visits, resort stays, and travelers meeting boats or friends on the east coast.",
      href: "/cyprus",
    },
    {
      name: "Ayia Napa resort center",
      text:
        "A practical destination for hotels, apartments, nightlife, restaurants, harbour visits, and central holiday accommodation.",
      href: "/cyprus",
    },
  ],
  destinationsSection: {
    eyebrow: "Pickup and resort drop-offs",
    title: "Popular Limassol starting points and Ayia Napa destinations",
    text:
      "For this route, the most helpful booking details are the exact Limassol pickup point, the Ayia Napa hotel or villa name, luggage volume, and whether the trip is for a beach day, resort check-in, event, or late-night return.",
  },
  internalLinks: [
    {
      label: "Taxi Limassol",
      href: "/taxi-limassol",
    },
    {
      label: "Limassol Hotels",
      href: "/limassol-hotels",
    },
    {
      label: "Things To Do In Limassol",
      href: "/things-to-do-in-limassol",
    },
    {
      label: "Cyprus Guide",
      href: "/cyprus",
    },
  ],
  faqs: [
    {
      question: "Can I book a private taxi from Limassol to Ayia Napa?",
      answer:
        "Yes. You can book a private door-to-door taxi from Limassol to Ayia Napa for hotels, villas, beach days, nightlife, marina visits, family trips, and resort transfers.",
    },
    {
      question: "Is the Limassol to Ayia Napa taxi price fixed?",
      answer:
        "The listed fare is a guide for a standard private transfer. The confirmed price depends on the exact pickup, Ayia Napa drop-off, passenger count, luggage, pickup time, and any return arrangements.",
    },
    {
      question: "What time should I leave Limassol for Ayia Napa?",
      answer:
        "Departure time depends on your check-in, dinner booking, event, or beach plan. During summer weekends and busy evenings, allow extra time for motorway traffic and resort roads near Ayia Napa.",
    },
    {
      question: "Can the taxi drop us at Nissi Beach or Ayia Napa Marina?",
      answer:
        "Yes. Drop-offs can be arranged for Nissi Beach, Ayia Napa Marina, the harbour, central hotels, villa areas, and resort neighborhoods. Share the exact location or map pin when booking.",
    },
    {
      question: "Is this route suitable for families with luggage?",
      answer:
        "Yes. Families often book this route for luggage space, child seat requests, pushchairs, and a direct journey to resort accommodation. Mention children and luggage before confirmation.",
    },
    {
      question: "Can I book a return taxi from Ayia Napa to Limassol?",
      answer:
        "Yes. Return transfers can be arranged in advance for same-day trips, late-night travel, hotel check-outs, events, or the end of a resort stay.",
    },
  ],
};

const limassolToProtarasTaxiContent = {
  intro: {
    eyebrow: "Limassol to Protaras taxi guide",
    title: "Private family resort transfers from Limassol to Protaras",
    text:
      "A Limassol to Protaras taxi is ideal for travelers heading to the east coast for family hotels, villas, calm beaches, and longer resort stays. A private transfer gives you a direct journey from your Limassol address to your Protaras accommodation without changing vehicles or coordinating shared transport.",
  },
  serviceCards: [
    {
      title: "Journey overview",
      text:
        "The journey from Limassol to Protaras crosses Cyprus toward the Famagusta district and continues beyond Ayia Napa to the Protaras resort area. Because the final part includes coastal resort roads, accurate drop-off details help keep the arrival smooth.",
    },
    {
      title: "Family resort travel",
      text:
        "Protaras is often chosen for family holidays, quieter evenings, shallow beaches, apartments, and villa stays. A private taxi is useful when traveling with children, beach equipment, groceries, or luggage for a longer stay.",
    },
    {
      title: "Hotels, villas, and beach transfers",
      text:
        "Transfers can be arranged to hotel receptions, self-catering apartments, private villas, and beachside accommodation. Villa names, gate codes, or map pins are especially helpful for properties away from the main hotel road.",
    },
    {
      title: "Fig Tree Bay and Pernera",
      text:
        "Common drop-offs include Fig Tree Bay, Pernera, Kapparis, central Protaras hotels, and coastal streets near family resorts. If you are meeting relatives or checking into a villa, share the exact landmark before travel.",
    },
  ],
  detailSection: {
    eyebrow: "Protaras transfer planning",
    title: "A direct taxi for relaxed east coast resort travel",
    text:
      "Protaras transfers are usually about comfort, luggage space, and a calm arrival rather than nightlife timing. The driver collects you from Limassol and takes you directly to the hotel, villa, apartment, or beach area you specify.",
  },
  journeyCards: [
    {
      title: "Fixed price private transfer",
      text:
        "The published fare gives a clear guide before booking. Your confirmed price should include the Limassol pickup, Protaras drop-off, passenger count, luggage, child seat needs, travel time, and any return journey.",
    },
    {
      title: "Door-to-door pickup",
      text:
        "Pickup can be arranged from Limassol hotels, homes, offices, serviced apartments, Limassol Marina, or beachfront accommodation. A clear pickup point helps the driver avoid delays at large resorts or residential complexes.",
    },
    {
      title: "Best departure times",
      text:
        "For hotel check-in, dinner reservations, or villa handovers, allow extra time during summer weekends and holiday periods. Traffic can build near Limassol exits, Larnaca approaches, and the resort roads around Protaras.",
    },
    {
      title: "Luggage and passenger comfort",
      text:
        "This route suits passengers carrying suitcases, beach bags, pushchairs, sports equipment, or groceries for a villa stay. Mention luggage volume early so the vehicle can be matched to the group properly.",
    },
    {
      title: "Return transfers",
      text:
        "Return taxis from Protaras to Limassol can be arranged for hotel check-out, airport connections, family visits, or the end of a holiday. Booking the return in advance helps avoid last-minute transport searches.",
    },
    {
      title: "Protaras vs Ayia Napa intent",
      text:
        "Ayia Napa trips often focus on nightlife, beach clubs, and marina plans. Protaras journeys are more commonly booked for family resorts, villas, calmer beaches, and longer relaxed stays on the east coast.",
    },
  ],
  popularDestinations: [
    {
      name: "Limassol Marina pickup",
      text:
        "A useful starting point for central apartments, restaurants, old port stays, and visitors leaving from the city center.",
      href: "/things-to-do-in-limassol",
    },
    {
      name: "Limassol hotels",
      text:
        "Common pickups include beachfront hotels, Germasogeia accommodation, Agios Tychonas resorts, and business hotels.",
      href: "/limassol-hotels",
    },
    {
      name: "Residential Limassol",
      text:
        "Door-to-door pickup works well for homes, serviced apartments, residential complexes, and family addresses.",
      href: "/taxi-limassol",
    },
    {
      name: "Fig Tree Bay",
      text:
        "A well-known Protaras beach area for family hotels, apartments, beach days, and resort stays near the seafront.",
      href: "/cyprus",
    },
    {
      name: "Pernera",
      text:
        "A popular nearby resort area for villas, quieter accommodation, family trips, restaurants, and coastal walks.",
      href: "/cyprus",
    },
    {
      name: "Kapparis and coastal villas",
      text:
        "Useful for villa stays, private accommodation, and travelers staying slightly outside central Protaras.",
      href: "/cyprus",
    },
  ],
  destinationsSection: {
    eyebrow: "Pickup and resort drop-offs",
    title: "Popular Limassol starting points and Protaras destinations",
    text:
      "For a Protaras transfer, provide the full hotel, villa, apartment, or beach area details before confirmation. This is especially important for private accommodation around Pernera, Kapparis, and coastal roads where addresses can be less obvious.",
  },
  internalLinks: [
    {
      label: "Taxi Limassol",
      href: "/taxi-limassol",
    },
    {
      label: "Limassol Hotels",
      href: "/limassol-hotels",
    },
    {
      label: "Cyprus Guide",
      href: "/cyprus",
    },
    {
      label: "Cyprus Airport Transfers",
      href: "/cyprus-airport-transfers",
    },
  ],
  faqs: [
    {
      question: "Can I book a private taxi from Limassol to Protaras?",
      answer:
        "Yes. You can book a private door-to-door taxi from Limassol to Protaras for hotels, villas, family holidays, beach stays, apartment check-ins, and return transfers.",
    },
    {
      question: "Is the Limassol to Protaras taxi price fixed?",
      answer:
        "The listed price is a guide for a standard private transfer. The confirmed fare depends on the pickup point, Protaras destination, passengers, luggage, child seats, timing, and return transfer needs.",
    },
    {
      question: "Can the driver take us to Fig Tree Bay or Pernera?",
      answer:
        "Yes. Drop-offs can be arranged for Fig Tree Bay, Pernera, central Protaras hotels, Kapparis, coastal villas, and nearby resort areas. A map pin is helpful for private accommodation.",
    },
    {
      question: "What time should we leave Limassol for Protaras?",
      answer:
        "Leave extra time during summer weekends, school holidays, and late afternoon travel. The route is long, and traffic can affect motorway sections and resort roads near Protaras.",
    },
    {
      question: "Is this route suitable for families with children?",
      answer:
        "Yes. Protaras is a popular family resort destination, and private taxis are useful for children, luggage, pushchairs, beach bags, and direct hotel or villa drop-off.",
    },
    {
      question: "How is Protaras travel different from Ayia Napa travel?",
      answer:
        "Protaras transfers are usually booked for family resorts, quieter beaches, villas, and relaxed holidays. Ayia Napa trips more often focus on nightlife, events, beach clubs, and marina plans.",
    },
    {
      question: "Can I book a return taxi from Protaras to Limassol?",
      answer:
        "Yes. Return transfers can be booked in advance for hotel check-out, villa departures, family visits, or same-day trips back to Limassol.",
    },
  ],
};

const limassolToTroodosTaxiContent = {
  intro: {
    eyebrow: "Limassol to Troodos taxi guide",
    title: "Private mountain transfers from Limassol to Troodos",
    text:
      "A Limassol to Troodos taxi is a flexible way to reach mountain villages, nature trails, monasteries, viewpoints, wineries, and seasonal escapes without renting a car. The journey is different from airport or resort transfers because road conditions, elevation, weather, and return planning all matter.",
  },
  serviceCards: [
    {
      title: "Journey overview",
      text:
        "The route from Limassol to Troodos climbs inland from the coast toward mountain roads and village areas. Travel time depends on the exact destination because Platres, Kakopetria, Omodos, Kykkos Monastery, and viewpoints sit in different parts of the range.",
    },
    {
      title: "Mountain village and nature travel",
      text:
        "Troodos trips are often booked for fresh air, traditional villages, hiking, wine routes, churches, waterfalls, and scenic stops. A private taxi helps visitors reach places that are harder to combine by public transport.",
    },
    {
      title: "Troodos day trips",
      text:
        "Many passengers travel from Limassol to Troodos and return the same day. Sharing your intended stops in advance helps plan a realistic route, especially if you want to include villages, lunch, walking time, and photo viewpoints.",
    },
    {
      title: "Winter and summer travel",
      text:
        "Summer trips often focus on cooler mountain air, villages, trails, and wineries. Winter journeys can involve colder weather, fog, wet roads, or snow near higher elevations, so pickup time and clothing matter more.",
    },
  ],
  detailSection: {
    eyebrow: "Mountain transfer planning",
    title: "A private taxi for villages, viewpoints, and nature stops",
    text:
      "Troodos transfers need more planning than a simple point-to-point city ride. The best booking details include your exact pickup, preferred village or attraction, return time, walking plans, luggage or gear, and whether you need short waiting stops along the way.",
  },
  journeyCards: [
    {
      title: "Fixed price private transfer",
      text:
        "The published fare gives a useful starting point for a standard transfer. Your confirmed price should reflect the exact Troodos destination, route complexity, waiting time, passenger count, luggage, walking gear, and return transfer plan.",
    },
    {
      title: "Door-to-door pickup",
      text:
        "Pickup can be arranged from Limassol hotels, homes, offices, Limassol Marina, seafront apartments, or residential addresses. Clear pickup details help the driver start the mountain route without delay.",
    },
    {
      title: "Best departure times",
      text:
        "Morning departures are usually best for day trips because they leave more time for villages, walking, lunch, and return travel before dark. In winter, earlier travel also helps with visibility and changing mountain conditions.",
    },
    {
      title: "Mountain road considerations",
      text:
        "Troodos roads can be narrow, winding, steep, or affected by weather. A private taxi is helpful for passengers who prefer not to drive unfamiliar mountain roads, especially after rain, fog, or during colder months.",
    },
    {
      title: "Luggage and walking gear",
      text:
        "If you are carrying backpacks, walking shoes, coats, camera equipment, picnic bags, or small suitcases, mention this before booking. The right vehicle choice improves comfort on the climb and return journey.",
    },
    {
      title: "Return transfers",
      text:
        "Return travel should be planned before leaving Limassol, especially for villages, monasteries, and viewpoints where taxis are not always waiting nearby. Agreeing a return time keeps the day trip simple.",
    },
    {
      title: "Different from resort transfers",
      text:
        "Resort transfers usually focus on hotels, beaches, nightlife, and luggage. Troodos travel is more about scenery, elevation, village access, weather, walking time, and flexible stops across mountain routes.",
    },
  ],
  popularDestinations: [
    {
      name: "Limassol Marina pickup",
      text:
        "A convenient starting point for visitors staying near the old port, central apartments, restaurants, and city hotels.",
      href: "/things-to-do-in-limassol",
    },
    {
      name: "Limassol hotels",
      text:
        "Common pickups include beachfront hotels, business hotels, Germasogeia accommodation, and Agios Tychonas resorts.",
      href: "/limassol-hotels",
    },
    {
      name: "Residential Limassol",
      text:
        "Door-to-door pickup is useful for homes, serviced apartments, offices, and families starting a mountain day trip.",
      href: "/taxi-limassol",
    },
    {
      name: "Platres",
      text:
        "A popular mountain village for cooler weather, nature walks, restaurants, hotels, and access to nearby trails.",
      href: "/cyprus",
    },
    {
      name: "Kakopetria",
      text:
        "A traditional village destination for old streets, mountain scenery, food stops, and relaxed day-trip itineraries.",
      href: "/cyprus",
    },
    {
      name: "Omodos",
      text:
        "A well-known wine village often visited for squares, wineries, monastery visits, lunch, and village walks.",
      href: "/cyprus",
    },
    {
      name: "Kykkos Monastery",
      text:
        "A longer mountain destination that needs careful timing because the route is more remote and return planning matters.",
      href: "/cyprus",
    },
    {
      name: "Mountain viewpoints",
      text:
        "Scenic stops can be included when agreed in advance, especially for photos, nature breaks, and short walks.",
      href: "/cyprus",
    },
  ],
  destinationsSection: {
    eyebrow: "Pickup and mountain drop-offs",
    title: "Popular Limassol starting points and Troodos destinations",
    text:
      "Troodos is not one single drop-off point, so the exact village, monastery, trail, restaurant, or viewpoint matters. Sharing your preferred stops before confirmation helps create a transfer plan that fits the day rather than just the distance.",
  },
  internalLinks: [
    {
      label: "Taxi Limassol",
      href: "/taxi-limassol",
    },
    {
      label: "Things To Do In Limassol",
      href: "/things-to-do-in-limassol",
    },
    {
      label: "Cyprus Guide",
      href: "/cyprus",
    },
    {
      label: "Limassol Hotels",
      href: "/limassol-hotels",
    },
  ],
  faqs: [
    {
      question: "Can I book a private taxi from Limassol to Troodos?",
      answer:
        "Yes. You can book a private taxi from Limassol to Troodos for mountain villages, viewpoints, monasteries, nature walks, wineries, restaurants, and day trips.",
    },
    {
      question: "Is the Limassol to Troodos taxi price fixed?",
      answer:
        "The listed fare is a guide for a standard transfer. The confirmed price depends on the exact mountain destination, waiting time, planned stops, passengers, luggage, road conditions, and return transfer needs.",
    },
    {
      question: "Can the taxi take us to Platres, Kakopetria, Omodos, or Kykkos Monastery?",
      answer:
        "Yes. Transfers can be arranged to Platres, Kakopetria, Omodos, Kykkos Monastery, and other Troodos villages or viewpoints. Share the full itinerary so timing can be planned properly.",
    },
    {
      question: "What time should we leave Limassol for a Troodos day trip?",
      answer:
        "A morning departure is usually best because it gives more daylight for villages, walking, lunch, photos, and the return to Limassol. Winter trips may need even more time for weather and visibility.",
    },
    {
      question: "Is Troodos travel different in winter and summer?",
      answer:
        "Yes. Summer trips often focus on cooler mountain air, villages, and trails. Winter trips may involve colder temperatures, wet roads, fog, or snow near higher areas, so planning is more important.",
    },
    {
      question: "Can we bring walking gear or luggage?",
      answer:
        "Yes. Mention backpacks, coats, walking shoes, camera equipment, picnic bags, or suitcases before booking so the vehicle can be planned around passenger comfort and storage space.",
    },
    {
      question: "Can I book a return taxi from Troodos to Limassol?",
      answer:
        "Yes. Return transfers should be arranged in advance because mountain villages and viewpoints do not always have taxis waiting nearby. Agreeing the return time before departure is recommended.",
    },
  ],
};

export default function AirportTransferPage({
  airportName,
  routeTitle,
  price,
  description,
  canonicalUrl,
  ogUrl,
}) {
  const heroImage = getDestinationHeroImage(routeTitle);
  const pageUrl = ogUrl || canonicalUrl;
  const routeContentByUrl = {
    "https://limassolairporttaxi.com/larnaca-airport-taxi":
      larnacaAirportTaxiContent,
    "https://limassolairporttaxi.com/paphos-airport-taxi":
      paphosAirportTaxiContent,
    "https://limassolairporttaxi.com/limassol-to-nicosia":
      limassolToNicosiaTaxiContent,
    "https://limassolairporttaxi.com/limassol-to-ayia-napa":
      limassolToAyiaNapaTaxiContent,
    "https://limassolairporttaxi.com/limassol-to-protaras":
      limassolToProtarasTaxiContent,
    "https://limassolairporttaxi.com/limassol-to-troodos":
      limassolToTroodosTaxiContent,
  };
  const routeContent = routeContentByUrl[canonicalUrl] || null;
  const whatsappLink =
    "https://wa.me/35796613191?text=Hello,%20I%20would%20like%20to%20book%20a%20taxi.";

  const cardClass =
    "rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.025] p-6 shadow-xl shadow-black/25 transition-all duration-300 hover:-translate-y-1 hover:border-yellow-400/50 hover:shadow-yellow-400/10";
  const primaryButtonClass =
    "inline-flex items-center justify-center rounded-2xl bg-yellow-400 px-8 py-4 text-center font-extrabold text-black shadow-xl shadow-yellow-400/20 transition-all duration-200 hover:-translate-y-1 hover:bg-yellow-300 hover:shadow-yellow-400/30";
  const secondaryButtonClass =
    "inline-flex items-center justify-center rounded-2xl border border-yellow-400/25 bg-white/[0.06] px-8 py-4 text-center font-extrabold text-white backdrop-blur transition-all duration-200 hover:-translate-y-1 hover:border-yellow-400/60 hover:text-yellow-300";

  const benefits = [
    "24/7 availability",
    "Fixed prices",
    "Clean vehicles",
    "Professional drivers",
    "Fast WhatsApp booking",
    "Airport transfers",
  ];

  const bookingSteps = [
    {
      number: "01",
      title: "Send your route",
      text: `Tell us your pickup point, destination, date, time, and flight details for your ${airportName} transfer.`,
    },
    {
      number: "02",
      title: "Get confirmation",
      text: "We confirm your taxi, price, pickup time, and driver details by phone or WhatsApp.",
    },
    {
      number: "03",
      title: "Travel comfortably",
      text: "Your driver arrives on time and takes you directly to the airport or your destination.",
    },
  ];

  const reasons = [
    "Private door-to-door airport transfers",
    "Clear communication before pickup",
    "Comfortable taxis for families, tourists, and business travelers",
    "Reliable service for early morning and late night flights",
  ];

  const faqs = [
    {
      question: `Do you provide transfers to ${airportName} 24/7?`,
      answer:
        "Yes. Airport transfers are available day and night, including early morning departures and late arrivals.",
    },
    {
      question: "Is the price fixed?",
      answer:
        "The advertised route price is a guide for standard transfers. The final price can depend on exact pickup location, luggage, passengers, holidays, and special requests.",
    },
    {
      question: "Can I book by WhatsApp?",
      answer:
        "Yes. WhatsApp is the fastest way to send your route, flight details, and pickup time for confirmation.",
    },
    {
      question: "Do you track flight details?",
      answer:
        "You can send your flight number when booking so the driver has the correct airport pickup information.",
    },
  ];
  const pageFaqs = routeContent?.faqs || faqs;

  return (
    <main className="min-h-screen overflow-hidden bg-[#030712] text-white">
      <Helmet>
        <title>{routeTitle} | Limassol Airport Taxi</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content={`${routeTitle} | Limassol Airport Taxi`}
        />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta
          property="og:image"
          content="https://limassolairporttaxi.com/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${routeTitle} | Limassol Airport Taxi`}
        />
        <meta name="twitter:description" content={description} />
        <meta
          name="twitter:image"
          content="https://limassolairporttaxi.com/og-image.jpg"
        />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <section className="relative overflow-hidden px-6 pb-20 pt-36 md:pb-28 md:pt-44">
        <img
          src={heroImage}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-black/15" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_22%_18%,rgba(250,204,21,0.12),transparent_34%),linear-gradient(115deg,rgba(0,0,0,0.5),rgba(0,0,0,0.35)_48%,rgba(0,0,0,0.5))]" />
        <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#030712]/95 to-transparent" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-[1.15fr_0.85fr]">
          <div>
            <p className="inline-flex rounded-full border border-yellow-400/30 bg-yellow-400/10 px-5 py-2 text-sm font-bold uppercase tracking-[0.18em] text-yellow-300 shadow-lg shadow-yellow-400/10">
              Limassol Airport Taxi
            </p>

            <h1 className="mt-6 text-4xl font-black leading-[1.02] tracking-tight text-white md:text-7xl">
              {routeTitle}
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-8 text-neutral-300">
              {description}
            </p>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <a
                href="/#booking"
                onClick={() => trackBookingClick("airport_transfer_hero")}
                className={primaryButtonClass}
              >
                Book Taxi Now
              </a>

              <a
                href={whatsappLink}
                onClick={() => trackWhatsAppClick("airport_transfer_hero")}
                target="_blank"
                rel="noreferrer"
                className={secondaryButtonClass}
              >
                WhatsApp Booking
              </a>
            </div>
          </div>

          <div className="rounded-[2rem] border border-yellow-400/25 bg-gradient-to-br from-white/[0.11] via-white/[0.05] to-yellow-400/[0.035] p-6 shadow-2xl shadow-black/40 backdrop-blur">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-neutral-400">
              Private transfer
            </p>

            <div className="mt-5 rounded-[1.5rem] border border-yellow-400/35 bg-yellow-400 p-6 text-black shadow-xl shadow-yellow-400/20">
              <p className="text-black/70">Route price</p>
              <p className="mt-3 text-5xl font-black text-black">
                {price}
              </p>
              <p className="mt-4 text-sm font-medium leading-6 text-black/75">
                Direct taxi transfer for {airportName}. Ideal for hotels,
                apartments, business trips, family travel, and airport pickups.
              </p>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {[
                ["Distance", "Direct route", FaIcons.FaRoute],
                ["Travel Time", "Route dependent", FaIcons.FaClock],
                ["From Price", price, FaIcons.FaTags],
                ["Availability", "24/7 by request", FaIcons.FaPlane],
              ].map(([label, value, Icon]) => (
                  <div
                    key={label}
                    className="rounded-2xl border border-white/10 bg-black/25 p-4 shadow-lg shadow-black/10"
                  >
                    <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-yellow-400/10 text-yellow-300">
                      <Icon />
                    </div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-yellow-300">
                      {label}
                    </p>
                    <p className="mt-2 text-sm font-bold leading-6 text-white">
                      {value}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 md:py-24">
        <div className="grid gap-10 lg:grid-cols-[0.86fr_1.14fr] lg:items-start">
          <div className="mb-4 max-w-3xl lg:mb-0">
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">Route benefits</p>
            <h2 className="mt-4 text-3xl font-black tracking-tight md:text-5xl">
              A smoother way to travel between Limassol and {airportName}
            </h2>
          </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {benefits.map((benefit) => (
            <div
              key={benefit}
              className={cardClass}
            >
              <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-2xl bg-yellow-400 font-bold text-black shadow-lg shadow-yellow-400/20">
                ✓
              </div>
              <h3 className="text-xl font-bold text-yellow-300">
                {benefit}
              </h3>
            </div>
          ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-10 md:py-12">
        <div className="grid gap-6 rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.025] p-6 shadow-2xl shadow-black/25 backdrop-blur md:grid-cols-[0.9fr_1.1fr] md:p-10">
          <div className="rounded-[1.5rem] bg-yellow-400 p-8 text-black shadow-xl shadow-yellow-400/20">
            <p className="font-semibold">Transfer price</p>
            <p className="mt-4 text-5xl font-bold">{price}</p>
            <p className="mt-5 text-sm font-medium leading-6">
              Standard private transfer estimate for this route.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {[
              ["Airport", airportName],
              ["Service", "Private taxi transfer"],
              ["Booking", "Phone or WhatsApp"],
              ["Availability", "24/7 by request"],
            ].map(([label, value]) => (
              <div
                key={label}
                className="rounded-2xl border border-white/10 bg-black/30 p-5"
              >
                <p className="text-sm text-neutral-400">{label}</p>
                <p className="mt-2 font-bold text-white">{value}</p>
              </div>
            ))}

            <p className="sm:col-span-2 text-sm leading-6 text-neutral-400">
              Final price may depend on pickup time, passengers, luggage,
              public holidays, and exact pickup or destination address.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 md:py-24">
        <div className="mb-10 text-center">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">How booking works</p>
          <h2 className="mt-4 text-3xl font-black tracking-tight md:text-5xl">
            Book your airport taxi in three simple steps
          </h2>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {bookingSteps.map((step) => (
            <div
              key={step.number}
              className={cardClass}
            >
              <p className="text-4xl font-bold text-yellow-300">
                {step.number}
              </p>
              <h3 className="mt-6 text-2xl font-bold">{step.title}</h3>
              <p className="mt-4 leading-7 text-neutral-400">{step.text}</p>
            </div>
          ))}
        </div>
      </section>

      {routeContent && (
        <section className="mx-auto max-w-7xl px-6 py-20 md:py-24">
          <div className="mb-10 max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
              {routeContent.intro.eyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-black tracking-tight md:text-5xl">
              {routeContent.intro.title}
            </h2>
            <p className="mt-6 leading-8 text-neutral-300">
              {routeContent.intro.text}
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            {routeContent.serviceCards.map((item) => (
              <div key={item.title} className={cardClass}>
                <h3 className="text-2xl font-black text-yellow-300">
                  {item.title}
                </h3>
                <p className="mt-4 leading-7 text-neutral-400">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {routeContent && (
        <section className="relative bg-[#09090b] px-6 py-20 md:py-24">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.82fr_1.18fr]">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
                {routeContent.detailSection.eyebrow}
              </p>
              <h2 className="mt-4 text-3xl font-black tracking-tight md:text-5xl">
                {routeContent.detailSection.title}
              </h2>
              <p className="mt-6 leading-8 text-neutral-300">
                {routeContent.detailSection.text}
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {routeContent.journeyCards.map((item) => (
                <div key={item.title} className={cardClass}>
                  <h3 className="text-xl font-bold text-yellow-300">
                    {item.title}
                  </h3>
                  <p className="mt-4 leading-7 text-neutral-400">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {routeContent && (
        <section className="mx-auto max-w-7xl px-6 py-20 md:py-24">
          <div className="mb-10 text-center">
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
              {routeContent.destinationsSection.eyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-black tracking-tight md:text-5xl">
              {routeContent.destinationsSection.title}
            </h2>
            <p className="mx-auto mt-5 max-w-3xl leading-8 text-neutral-300">
              {routeContent.destinationsSection.text}
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {routeContent.popularDestinations.map((destination) => (
              <a
                key={destination.name}
                href={destination.href}
                className={cardClass}
              >
                <h3 className="text-xl font-black text-yellow-300">
                  {destination.name}
                </h3>
                <p className="mt-4 leading-7 text-neutral-400">
                  {destination.text}
                </p>
              </a>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {routeContent.internalLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="rounded-2xl border border-yellow-400/25 bg-white/[0.06] px-5 py-3 text-sm font-bold text-white transition-colors hover:border-yellow-400/60 hover:text-yellow-300"
              >
                {link.label}
              </a>
            ))}
          </div>
        </section>
      )}

      <section className="relative bg-[#09090b] px-6 py-20 md:py-24">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">Why choose us</p>
            <h2 className="mt-4 text-3xl font-black tracking-tight md:text-5xl">
              Premium airport transfers with clear, reliable service
            </h2>
            <p className="mt-6 leading-8 text-neutral-300">
              Limassol Airport Taxi focuses on punctual pickups, clean
              vehicles, professional drivers, and fast communication. Whether
              you are arriving in Cyprus or catching a flight, your transfer is
              handled with care from booking to drop-off.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {reasons.map((reason) => (
              <div
                key={reason}
                className={cardClass}
              >
                <p className="font-semibold text-neutral-200">{reason}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 md:py-24">
        <div className="rounded-[2rem] border border-yellow-400/25 bg-gradient-to-br from-yellow-400/20 via-white/[0.055] to-black/30 p-8 text-center shadow-2xl shadow-yellow-400/10 md:p-12">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
            Fast confirmation
          </p>
          <h2 className="mx-auto mt-4 max-w-3xl text-3xl font-black tracking-tight md:text-5xl">
            Book Your Taxi Transfer Today
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-neutral-300">
            Get fast confirmation by phone or WhatsApp with fixed prices and 24/7 availability.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <a
              href="/#booking"
              onClick={() => trackBookingClick("airport_transfer_confirmation")}
              className={primaryButtonClass}
            >
              Book Taxi Now
            </a>
            <a
              href={whatsappLink}
              onClick={() => trackWhatsAppClick("airport_transfer_confirmation")}
              target="_blank"
              rel="noreferrer"
              className={secondaryButtonClass}
            >
              WhatsApp Booking
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20 md:py-24">
        <div className="mb-10 text-center">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">FAQ</p>
          <h2 className="mt-4 text-3xl font-black tracking-tight md:text-5xl">
            Common questions about this transfer
          </h2>
        </div>

        <div className="grid gap-5">
          {pageFaqs.map((faq) => (
            <div
              key={faq.question}
              className="rounded-[1.75rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.025] p-6 shadow-xl shadow-black/20 md:p-7"
            >
              <h3 className="flex gap-3 text-xl font-black text-yellow-300">
                <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-yellow-400 text-xs font-black text-black">
                  ?
                </span>
                <span>
                {faq.question}
                </span>
              </h3>
              <p className="mt-4 leading-7 text-neutral-400 md:pl-9">{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 md:py-24">
        <div className="rounded-[2rem] border border-yellow-400/25 bg-gradient-to-br from-yellow-400/20 via-white/[0.055] to-black/30 p-8 text-center text-white shadow-2xl shadow-yellow-400/10 md:p-12">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
            Ready to book?
          </p>
          <h2 className="mx-auto mt-4 max-w-3xl text-3xl font-black tracking-tight md:text-5xl">
            Reserve your {airportName} taxi transfer now
          </h2>
          <p className="mx-auto mt-5 max-w-2xl font-medium leading-7 text-neutral-300">
            Call or send a WhatsApp message with your pickup location, date,
            time, passengers, luggage, and flight number.
          </p>

          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <a
              href="/#booking"
              onClick={() => trackBookingClick("airport_transfer_final_cta")}
              className={primaryButtonClass}
            >
              Book Taxi Now
            </a>

            <a
              href={whatsappLink}
              onClick={() => trackWhatsAppClick("airport_transfer_final_cta")}
              target="_blank"
              rel="noreferrer"
              className={secondaryButtonClass}
            >
              WhatsApp Booking
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
